import React, { useContext, useEffect, useMemo, useState } from "react";
import GroupStarterChart from "./GroupStarterChart";
import HeaderPreview from "../../../GeneratePdf/HeaderPreview";
import FooterPreview from "../../../GeneratePdf/FooterPreview";
import GroupReportContext from "../../../../Context/GroupReportContext";
import { useGroupReportDate } from "../../../../Context/GroupReportDate";
import { initialData } from "./QuadrantInFocus";

const GroupStarterQuadrantChartPreview = ({ showNote = false, apiData, previewData, onDataCalculated, reportType, pageNumber, }) => {
  const groupReportCtx = useContext(GroupReportContext);
  const { averagePercentages, calculateQuestionScore, selectedReport } = groupReportCtx;
  const { updateStartChartdata } = useGroupReportDate();

  const [snapshotResult, setSnapshotResults] = useState([]);
  const [starterLabel, setStarterLabel] = useState([ "strength", "heart", "soul", "mind", ]);
  const [winnerQuadrant, setWinnerQuadrant] = useState(null); 
  const [topThreeStatements, setTopThreeStatements] = useState([]);
  const [dynamicData, setDynamicData] = useState([...initialData]);

  const displayData = dynamicData?.filter((el) => el.quadrant == winnerQuadrant?.toUpperCase());
  // console.log("winnerQuadrant",winnerQuadrant,dynamicData,displayData)
  const allVariableSets = useMemo(
    () =>
      Array.isArray(apiData?.results)
        ? apiData.results.map((el) => el.variables)
        : [],
    [apiData?.results]
  );

  const quadrantScores = {
    "HEART": 1,
    "SOUL": 2,
    "MIND": 3,
    "STRENGTH": 4
  }

  const allResults = [];
  displayData?.forEach((el, index) => {
    // console.log(el.quadrant, quadrantScores?.[el.quadrant])
    const res = groupReportCtx?.calculateQuestionScore(quadrantScores?.[el.quadrant], allVariableSets, index + 1, "in_focus");
    if (res?.averageScore !== null && quadrantScores?.[el.quadrant] == quadrantScores[winnerQuadrant?.toUpperCase()]) {
      allResults.push({
        ...res,
        question: el.question
      });
    }
  })

  const n = (v) => (Number.isFinite(+v) ? +v : 0);



  const styles = {
    coverSection: { textAlign: "left", fontFamily: "Helvetica, sans-serif", margin: "16px 0px",  display: "flex", flexDirection: "column", },
    coverTitle: { marginBottom: "4px", marginTop: 0, fontSize: "24px", fontWeight: 700, lineHeight: "48px", color: "#426b88", },
    secondSection: { marginTop: "1px", width: "100%", alignItems: "flex-start", justifyContent: "flex-start", fontFamily: "Helvetica, sans-serif", },
    subtitle: { fontSize: "14px", lineHeight: "18px", fontWeight: 700, color: "#00577D", display: "flex", justifyContent: "flex-start", marginTop: 0, marginBottom: "16px", },
    paragraph: { fontSize: "16px", fontWeight: 400, color: "#00577D", lineHeight: "24px", textAlign: "left", marginBottom: "5px", marginTop: 0, },
    imageContainer: { display: "flex", flexDirection: "row", justifyContent: "center", gap: 24, marginTop: "24px", marginBottom: "3px", width: "100%", },
    strongestHeading: { fontSize: "19px", lineHeight: "24px", color: "#00577D", fontWeight: 700, textTransform: "uppercase", marginBottom: "10px", },
    strongestList: { paddingLeft: "18px" },
    strongestListItem: { fontFamily: "'Helvetica', sans-serif", fontWeight: 400, fontSize: "16px", lineHeight: "24px", color: "#00577D", marginBottom: "20px", },
    noteContainer: { marginTop: "5px", padding: "2px 0" },
    noteBold: { fontSize: "12px", fontWeight: 700, color: "#245775" },
    noteText: { fontSize: "12px", fontWeight: 400, color: "#426B88", lineHeight: "1.5", letterSpacing: "0", },
  };
  
  const QUADRANT_BY_INDEX = ["Strength", "Heart", "Soul", "Mind"];
  const QUADRANT_ID = { Strength: 4, Heart: 1, Soul: 2, Mind: 3 };

  const QUESTION_BANK = {
    Strength: [
      "When presented with a problem, I immediately start working on it.",
      "I make decisions by focusing on what needs to get done.",
      "I feel most fulfilled when I get my tasks completed.",
      "I am motivated by winning.",
      "I rely heavily on my drive to complete a project.",
      "I respect people who deliver results.",
      "I gain the most satisfaction when I reach my goals.",
      "I am motivated to perform when given measurable goals.",
      "I like turning good ideas into action.",
      "I take satisfaction in a hard day's work.",
    ],
    Heart: [
      "When presented with a problem, I am most concerned with how the team is handling it.",
      "I ensure my decisions are measured by whether everyone is on board.", 
      "I feel most fulfilled when I am surrounded by my people.", 
      "I am motivated when I feel a sense of belonging.", 
      "I rely heavily on passion when working on a project.", 
      "I believe a collaborative team is the best way to achieve results.", 
      "I want to feel connected to others when I am doing something.", 
      "When working on a project, I can become too emotionally invested.", 
      "My strong feelings sometimes overwhelm my good judgment.", 
      "I serve people first.", 
    ],
    Soul: [
      "My superpower is to rise above any situation to gain a wise perspective.", 
      "I evaluate decisions by their ability to fulfill the greater vision.", 
      "I am most fulfilled when I feel like I am living out my greater purpose.", 
      "I am motivated by opportunities that develop depth of character.", 
      "I rely heavily on the meaning of my story when working on a project.", 
      "I believe our work should connect to a larger purpose.", 
      "I am always searching for the reasons why I am doing something.", 
      "I struggle to stay engaged when the work has no clear purpose.", 
      "I am drawn to people who are visionaries.", 
      "I find satisfaction in sharing meaningful stories that impact people's lives."], 
    Mind: [
      "When presented with a problem, I need to understand the processes before I can start.", 
      "I make decisions based on the policies that guide me.", 
      "I am most fulfilled when a project works like it was planned.",
      "I am motivated when everything has been thoughtfully planned out.",
      "I need clear responsibilities to function well at work.",
      "My superpower is analyzing the solution to a problem.",
      "I find satisfaction in developing standardized procedures.",
      "When working on a project, I can get stuck by overthinking everything.",
      "I believe the right structures are essential for an effective organization.",
      "I find satisfaction in developing effective systems.",
    ],
  };


  const pickWinnerQuadrant = (scores) => {
    const ranked = scores
      .map((val, idx) => ({ idx, val: n(val), name: QUADRANT_BY_INDEX[idx] }))
      .sort((a, b) => (b.val - a.val) || (a.idx - b.idx));
    return ranked[0].name;
  };

  const computeTopThreeForQuadrant = (qName) => {
    const qId = QUADRANT_ID[qName];
    if (!qId || !calculateQuestionScore || !allVariableSets?.length) return [];

    const items = [];
    for (let no = 1; no <= 10; no++) {
      const res = calculateQuestionScore(qId, allVariableSets, no);
      const avg = res?.averageScore;
      if (avg !== null && avg !== undefined) {
        items.push({
          no,
          question: QUESTION_BANK[qName][no - 1] || `Item ${no}`,
          avg: Number(avg) || 0,
        });
      }
    }

    items.sort((a, b) => (b.avg - a.avg) || (a.no - b.no));
    return items.slice(0, 3);
  };

  useEffect(() => {
    const avg = [
      n(averagePercentages?.find((el) => el?.name === "Strength")?.avg),
      n(averagePercentages?.find((el) => el?.name === "Heart")?.avg),
      n(averagePercentages?.find((el) => el?.name === "Soul")?.avg),
      n(averagePercentages?.find((el) => el?.name === "Mind")?.avg),
    ].map((v) => Number(v.toFixed(2)) || 0);

    setSnapshotResults(avg);
    setStarterLabel(["strength", "heart", "soul", "mind"]);
  }, [averagePercentages]);


  useEffect(() => {
    if (snapshotResult && snapshotResult.length > 0) {
      updateStartChartdata(snapshotResult);
    }
  }, [snapshotResult, updateStartChartdata]);

  useEffect(() => {
    if (snapshotResult.length > 0) {
      const winner = pickWinnerQuadrant(snapshotResult);
      setWinnerQuadrant(winner);
      const top3 = computeTopThreeForQuadrant(winner);
      const sortedAllresults = allResults?.sort((a,b) => b.averageScore - a.averageScore )
      setTopThreeStatements(sortedAllresults?.splice(0,3))
      // console.log("sortedAllresults",sortedAllresults)
      // setTopThreeStatements(top3);
    }
  }, [snapshotResult, allVariableSets, calculateQuestionScore, groupReportCtx]);


  // console.log("averagePercentages,selectedReport",averagePercentages,selectedReport)

  return (
    <div>
      <HeaderPreview />

      <div style={styles.coverSection}>
        {/* <div>
          <h2 style={styles.coverTitle}>GROUP STARTER QUADRANT</h2>
          <p style={styles.paragraph}> Every group has a unique quadrant of origin—a "home base" shaped by both nature and nurture—where they feel most at ease, authentic, and instinctive. Unlike many personality systems in use today, these four quadrants offer a fresh way of understanding how groups express themselves in the world. They offer valuable insights into how we love, live, work, contribute, and lead, both personally and professionally. </p>
          <p style={styles.paragraph}> A group's starting quadrant highlights the characteristics that feel most authentic to their natural, primary, or dominant way of being—the stance or approach that most reflect their instinctive way of relating to themselves and others. </p>
          <p style={styles.paragraph}> Consider how the group is perceived by others, and try to separate the group's perceived ideal from its genuine identity. Remember, no quadrant is superior to another; each is essential for achieving wholeness and health within the group. While all four quadrants play a crucial role, one often emerges as the primary starting point. </p>
          <p style={styles.paragraph}> Combining the scores of each individual allows for a collective result to emerge. This Starter Quadrant reflects the most natural or strongest energy and behaviors that drive the group. Comparing an individual's result with that of the team or group fosters a deeper understanding of their feelings and experiences of connection, appreciation, and alignment with others. </p>
        </div> */}

        <div style={styles.secondSection}>
          <h3 style={styles.subtitle}>GROUP STARTER QUADRANT OVERVIEW CHART</h3>

          <div style={styles.imageContainer}>
            <GroupStarterChart
              originalData={ snapshotResult.length > 0 ? snapshotResult : [0, 0, 0, 0] }
              labels={starterLabel}
              ch="mind"
            />

            {/* {winnerQuadrant && (
              <div>
                <h4 style={styles.strongestHeading}> STRONGEST CONFIRMATIONS <br /> AS A {winnerQuadrant.toUpperCase()}-STARTER </h4>

                <ol style={styles.strongestList}>
                  {topThreeStatements.length > 0
                    ? topThreeStatements.map((item) => (
                        <li style={styles.strongestListItem} key={item.no}> {item.question} </li>
                      ))
                    : 
                      QUESTION_BANK[winnerQuadrant].slice(0, 3).map((q, i) => (
                        <li style={styles.strongestListItem} key={i}> {q} </li>
                      ))}
                </ol>
              </div>
            )} */}
          </div>

          {/* {showNote && (
            <div style={styles.noteContainer}>
              <p>
                <span style={styles.noteBold}>Note: </span>
                <span style={styles.noteText}> Your group's profile reflects an even distribution across the Heart and Soul quadrants. </span>
              </p>
            </div>
          )} */}
        </div>
      </div>

      <FooterPreview pageNumber={pageNumber} reportType={reportType} />
    </div>
  );
};

export default GroupStarterQuadrantChartPreview;
